// Раздел «Задания движка»: на что человек ОТВЕЧАЕТ в своих бизнес-процессах.
//
// ЗАЧЕМ ЭТОТ ЭКРАН. Свой движок процессов уже работает: считает шаги, выдаёт задания,
// ведёт сроки и очередь разбора. Отвечать на задание было НЕГДЕ — экрана не существовало
// вовсе. Работа ставилась, а человек её не видел.
//
// ЧЕМ ОН ОТЛИЧАЕТСЯ ОТ «ЗАДАНИЙ ПРОЦЕССОВ». Тот раздел — зеркало Bitrix: чужой движок,
// закрыть можно только в Bitrix. Этот — свой движок. Подписи и порядок колонок повторены
// оттуда намеренно, чтобы при переходе никто не переучивался.
//
// ФОРМА ОТВЕТА ЕСТЬ, А ЗАПИСИ НЕТ, И ЭКРАН ГОВОРИТ ЭТО ВСЛУХ. Разделы CRM выходят
// сначала только на чтение: в ядре ручки записи нет вовсе (см. bpe_api.py), включение —
// отдельный шаг и отдельное решение владельца. Кнопка «Ответить» на экране есть, но она
// выключена и рядом написано почему. Молчаливо неработающая кнопка хуже отсутствующей:
// человек нажимает, ничего не происходит, и он уходит уверенный, что ответил.
//
// СРОК — ТРИ РАЗНЫХ СОСТОЯНИЯ, А НЕ ДВА. Пустой срок это НОРМА, а не просрочка: в
// шаблонах Bitrix, с которых сняты описания процессов, срок стоял у 29 заданий из 752
// (замер 29.07.2026). Состояние срока считает ядро одним выражением и присылает готовым
// (`due_state`) — экран его только называет словами. Считать здесь свою просрочку нельзя:
// два места сравнения дат разъедутся на границе суток, и «просрочено» вспыхнет там, где
// срока не было никогда.
//
// ПОКА ХРАНИЛИЩЕ ПУСТО, ЭКРАН ОБЪЯСНЯЕТ, ПОЧЕМУ. Ядро присылает `engine_started` и
// `why_empty` вместе с данными. Пустая таблица без объяснения читается как «работы нет»,
// и человек спокойно уходит от невыданной работы.
//
// ТРАНСПОРТ ЗДЕСЬ ЖЕ, А НЕ В src/api. Раздел пока один экран, и заводить ему отдельный
// файл транспорта смысла нет: когда включат запись, он всё равно переедет в api/bpe.ts
// вместе с ручкой ответа.

import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { useSelf } from '../api/self'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'

// ───────────────────────────── транспорт ─────────────────────────────

const БАЗА = '/api/bpe'

/** Тот же разбор ответа, что в api/client.ts и api/bp.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — отказ по существу фильтра. Пустая таблица читалась бы как «заданий
    // нет», а это ровно та ложь, ради которой раздел и заводился.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.why as string) || (rec.error as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

// ─────────── формы ответов ядра (см. bpe_api.py) ───────────

interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  /** Запускали ли движок вообще. Пустой список без этого прочитать нельзя. */
  engine_started: boolean
  why_empty?: string | null
  tasks_total?: number
  sort_note?: string | null
}

/** Состояние срока. Считает ядро; экран только называет его словами. */
type ВидСрока = 'закрыто' | 'без_срока' | 'просрочено' | 'сегодня' | 'в_срок'

interface Исполнитель {
  id: string
  name: string
  active: boolean | null
}

interface ЗаданиеСтрока {
  id: number
  instance_id: number | null
  kind: string | null
  name: string | null
  body: string | null
  mode: string | null
  percent: number | null
  entity: string | null
  doc_id: string | null
  due_at: string | null
  no_due_reason: string | null
  due_in_days: number | null
  due_state: ВидСрока
  created_at: string | null
  closed_at: string | null
  outcome: string | null
  age_days: number | null
  answers_len: number
  events_len: number
  need_answers: number
  assignees_n: number
  assignees_info: Исполнитель[]
  process: string | null
  instance_state: string | null
  instance_caption: string | null
}

interface ПолеФормы {
  name: string | null
  type: string
  type_known: boolean
  required: boolean
  options: string[] | null
  default: unknown
}

interface Ответ {
  who: string
  who_name: string | null
  who_active: boolean | null
  outcome: string | null
  comment: string | null
  at: string | null
}

interface Событие {
  task_id: number
  ord: number
  kind: string | null
  reason: string | null
  at: string | null
  who_before: string | null
  who_after: string | null
  who_before_name: string | null
  who_after_name: string | null
}

interface Карточка {
  row: ЗаданиеСтрока
  form: ПолеФормы[]
  answers: Ответ[]
  events: Событие[]
  write_enabled: boolean
  write_why: string
}

interface ЧеловекСтрока {
  who: string
  who_name: string | null
  who_active: boolean | null
  tasks: number
  approve: number
  review: number
  form: number
  no_due: number
  overdue: number
  due_today: number
  instances: number
  oldest_days: number | null
  median_days: number | null
}

interface ПроцессСтрока {
  process: string | null
  open_tasks: number
  instances: number
  no_due: number
  overdue: number
}

interface Сводка {
  rows: ЧеловекСтрока[]
  total: number
  by_process: ПроцессСтрока[]
  totals: { people: number; tasks: number; overdue: number; no_due: number }
  sum_note: string
  engine_started: boolean
  why_empty: string | null
}

interface РазборСтрока {
  id: number
  at: string | null
  kind: string | null
  ref: string | null
  note: string | null
  instance_id: number | null
  task_id: number | null
  process: string | null
  instance_state: string | null
  task_name: string | null
  age_days: number | null
  resolved_at: string | null
}

interface ВсталаСтрока {
  id: number
  process: string | null
  state: string | null
  caption: string | null
  updated_at: string | null
  open_tasks: number
}

interface Разбор extends Список<РазборСтрока> {
  stuck: ВсталаСтрока[]
  stuck_note: string
}

interface Справочники {
  counts: Record<string, number>
  freshness: Record<string, string | null>
  kinds: { kind: string | null; n: number; open: number }[]
  processes: { process: string | null; instances: number; live: number }[]
  people: { id: string; name: string; active: boolean | null; open: number }[]
  no_due_reasons: { reason: string | null; n: number }[]
  caveats: string[]
  engine_started: boolean
  why_empty: string | null
  write_enabled: boolean
  write_why: string
  kind_names: Record<string, string>
  outcome_names: Record<string, string>
  due_state_names: Record<string, string>
  field_type_names: Record<string, string>
  instance_state_names: Record<string, string>
}

// ─────────── хуки ───────────

const useСправочники = () =>
  useQuery({ queryKey: ['bpe', 'refs'], queryFn: () => запрос<Справочники>('/refs'), ...ОБЩЕЕ })

/**
 * `enabled` здесь не украшение. «Мои задания» без человека ядро не отдаёт вовсе — и
 * правильно делает. Спросить всё равно означало бы получить 400 и показать человеку
 * отказ вместо простого «выберите себя».
 */
const useЗадания = (ф: Record<string, unknown>, мои: boolean, включено: boolean) =>
  useQuery({
    queryKey: ['bpe', мои ? 'my' : 'all', ф],
    queryFn: () => запрос<Список<ЗаданиеСтрока>>(
      (мои ? '/my-tasks' : '/tasks') + строкаЗапроса(ф)),
    enabled: включено,
    ...ОБЩЕЕ,
  })

const useКарточка = (id: number | null) =>
  useQuery({
    queryKey: ['bpe', 'task', id],
    queryFn: () => запрос<Карточка>(`/task?id=${id ?? 0}`),
    enabled: !!id,
    ...ОБЩЕЕ,
  })

const useСводка = () =>
  useQuery({ queryKey: ['bpe', 'by-user'], queryFn: () => запрос<Сводка>('/by-user'), ...ОБЩЕЕ })

const useРазбор = () =>
  useQuery({ queryKey: ['bpe', 'review'], queryFn: () => запрос<Разбор>('/review'), ...ОБЩЕЕ })

const useИстория = (ф: Record<string, unknown>) =>
  useQuery({
    queryKey: ['bpe', 'events', ф],
    queryFn: () => запрос<Список<Событие>>('/task-events' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })

// ───────────────────────────── подписи ─────────────────────────────

const СТРАНИЦА = 50

/**
 * Ряд элементов в строку — та же раскладка, что у `.filters` в styles.css.
 *
 * Почему стилем, а не классом `.filters`. Этот класс в CRM означает «поля, сужающие
 * список», и проверка вида разделов следит, чтобы он не расползался: сужающие поля
 * обязаны стоять в FilterBar, который на телефоне сворачивается. Здесь же в ряд
 * выстроены вкладки и поля формы ответа — сворачивать их нечего и нельзя, а раскладка
 * нужна ровно та же. Своего класса ради двух мест не заводим: он потребовал бы правки
 * общего styles.css.
 */
const РЯД = {
  display: 'flex', gap: 'var(--s3)', flexWrap: 'wrap', alignItems: 'flex-end',
} as const

/**
 * Срок словами и цветом. Пять состояний, и «срока нет» среди них — отдельное, спокойное:
 * оно НЕ красное и никогда не называется просрочкой. Значение считает ядро — здесь только
 * подпись, потому что второе место сравнения дат однажды разойдётся с первым.
 */
function видСрока(р: ЗаданиеСтрока): { ярлык: string; класс: string; смысл: string } {
  switch (р.due_state) {
    case 'просрочено':
      return { ярлык: 'срок прошёл', класс: 'badge lose', смысл: `срок был ${fmtDate(р.due_at, true)}` }
    case 'сегодня':
      return { ярлык: 'срок сегодня', класс: 'badge work', смысл: `до ${fmtDate(р.due_at, true)}` }
    case 'в_срок':
      return { ярлык: 'в срок', класс: 'badge won', смысл: `срок ${fmtDate(р.due_at, true)}` }
    case 'закрыто':
      return { ярлык: 'закрыто', класс: 'badge', смысл: `закрыто ${fmtDate(р.closed_at, true)}` }
    default:
      return {
        ярлык: 'срока нет',
        класс: 'badge',
        // Почему срока нет — из самой строки: движок не примет задание без причины.
        смысл: р.no_due_reason
          ? `срок не назначали: ${р.no_due_reason}`
          : 'срок этому заданию не назначали — это не просрочка',
      }
  }
}

const видЧеловека = (активен: boolean | null) =>
  (активен === false ? { ярлык: 'не работает', класс: 'badge lose' } : null)

/** Кого ждём — строкой. Имена, а не номера: «10» ответом на вопрос «кто» не является. */
const ктоЖдёт = (люди: Исполнитель[]): string =>
  (люди.length ? люди.map((ч) => ч.name).join(', ') : '—')

// ───────────────────────────── экран ─────────────────────────────

type Вкладка = 'my' | 'all' | 'people' | 'review' | 'history'

const ВКЛАДКИ: { код: Вкладка; имя: string }[] = [
  { код: 'my', имя: 'Мои задания' },
  { код: 'all', имя: 'Все задания' },
  { код: 'people', имя: 'Кто чего ждёт' },
  { код: 'review', имя: 'Требует решения' },
  { код: 'history', имя: 'Переназначения' },
]

export default function BpeTasks() {
  const [вкладка, setВкладка] = useState<Вкладка>('my')
  const спр = useСправочники()
  const с = спр.data

  return (
    <>
      <div className="page-head">
        <h1>Задания движка</h1>
        <span className="spacer" />
        <span className="hint">
          {с ? `${pluralN(с.counts.tasks_open ?? 0, 'открытое задание', 'открытых задания',
            'открытых заданий')} · ${pluralN(с.counts.instances_live ?? 0,
            'живой процесс', 'живых процесса', 'живых процессов')}` : ''}
        </span>
      </div>

      {/* Движок не запускали — это первое, что человек обязан увидеть. Без этой строки
          пустой раздел читается как «на мне ничего нет». */}
      {с && !с.engine_started && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Заданий нет, потому что движок ещё не запускали.</b>
          <div style={{ marginTop: 6 }}>{с.why_empty}</div>
        </div>
      )}

      {/* Оговорки приходят от ядра вместе с данными и стоят ДО них: экран, который их
          спрячет, соврёт молча — «нет срока» тут не то же самое, что «просрочено». */}
      {с && с.caveats.length > 0 && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Как это читать.</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {с.caveats.map((т) => <li key={т}>{т}</li>)}
          </ul>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Вкладки кнопками в ряд — так же выглядят вкладки в «Заданиях процессов». */}
        <div style={РЯД}>
          {ВКЛАДКИ.map((в) => (
            <button key={в.код} type="button" onClick={() => setВкладка(в.код)}
                    disabled={вкладка === в.код}>
              {в.имя}
            </button>
          ))}
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          Ответ на задание пока не включён: раздел выходит на чтение. Форма показывает,
          что именно спросит движок, — отвечать в ней будет можно отдельным шагом.
        </div>
      </div>

      {вкладка === 'my' && <Задания мои спр={с} />}
      {вкладка === 'all' && <Задания мои={false} спр={с} />}
      {вкладка === 'people' && <КтоЖдёт />}
      {вкладка === 'review' && <Разбор />}
      {вкладка === 'history' && <История />}
    </>
  )
}

// ───────────────────────── вкладки «Мои» и «Все задания» ─────────────────────────

function Задания({ мои, спр }: { мои: boolean; спр?: Справочники }) {
  const я = useSelf()
  const [who, setWho] = useState('')
  const [kind, setKind] = useState('')
  const [process, setProcess] = useState('')
  const [due, setDue] = useState('')
  const [state, setState] = useState('open')
  const [q, setQ] = useState('')
  const [left, setLeft] = useState(false)
  const [start, setStart] = useState(0)
  const [открыто, setОткрыто] = useState<number | null>(null)

  // «Мои» — это привязка учётки к сотруднику Bitrix из сессии (/api/me), и она умолчание:
  // исполнители у движка записаны идентификаторами Bitrix, тот же номер подходит в `who`.
  // Селект остаётся ролям head/admin (посмотреть задания подчинённого — законно) и работает
  // как переопределение; у роли user его нет — вопрос «кто вы?» ей задавать не за чем.
  const кто = мои ? ((я.canPickOthers ? who : '') || я.id) : who

  const фильтры = useMemo(() => ({
    who: кто || undefined,
    kind: kind || undefined,
    process: process || undefined,
    due: due || undefined,
    state,
    q: q.trim() || undefined,
    left: left || undefined,
    start,
    limit: СТРАНИЦА,
  }), [кто, kind, process, due, state, q, left, start])

  // «Мои задания» без человека ядро не отдаёт вовсе, и это правильно: подставлять сюда
  // «кого-нибудь» значило бы показать чужую работу как свою. Привязка из сессии — не
  // «кто-нибудь», это и есть человек. А вот пока ни привязки, ни выбора нет, запрос не
  // уходит совсем — иначе экран показал бы отказ ядра вместо простого объяснения.
  const готово = !мои || !!кто
  const зпр = useЗадания(фильтры, мои, готово)
  const д = зпр.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id={мои ? 'bpe-my' : 'bpe-all'}>
          {/* На вкладке «Мои» селект видят только роли с правом смотреть чужое: у роли
              user человек уже известен из сессии, и спрашивать его не о чем. */}
          {(!мои || я.canPickOthers) && (
            <div className="f" style={{ minWidth: 220 }}>
              <label htmlFor="bpew">Сотрудник</label>
              <select id="bpew" value={мои ? кто : who}
                      onChange={(e) => сСброса(setWho)(e.target.value)}
                      disabled={!спр}>
                <option value="">{мои ? (я.id ? '— я сам —' : '— выберите себя —') : 'любой'}</option>
                {(спр?.people ?? []).map((ч) => (
                  <option key={ч.id} value={ч.id}>
                    {ч.name} ({ч.open}){ч.active === false ? ' — не работает' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="f" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="bpeq">Задание или документ</label>
            <input id="bpeq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: счёт или отпуск" />
          </div>
          <div className="f">
            <label htmlFor="bpek">Вид задания</label>
            <select id="bpek" value={kind} onChange={(e) => сСброса(setKind)(e.target.value)}>
              <option value="">любой</option>
              {(спр?.kinds ?? []).map((в) => (
                <option key={в.kind ?? ''} value={в.kind ?? ''}>
                  {спр?.kind_names[в.kind ?? ''] ?? fmtText(в.kind)} ({в.open})
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="bpep">Процесс</label>
            <select id="bpep" value={process}
                    onChange={(e) => сСброса(setProcess)(e.target.value)}>
              <option value="">любой</option>
              {(спр?.processes ?? []).map((п) => (
                <option key={п.process ?? ''} value={п.process ?? ''}>
                  {fmtText(п.process)} ({п.live})
                </option>
              ))}
            </select>
          </div>
          {/* Отбор по сроку. «Срока нет» — обычный пункт наравне с остальными, а не
              подвид просрочки: заданий без срока в процессах большинство. */}
          <div className="f">
            <label htmlFor="bped">Срок</label>
            <select id="bped" value={due} onChange={(e) => сСброса(setDue)(e.target.value)}>
              <option value="">любой</option>
              <option value="overdue">срок прошёл</option>
              <option value="today">срок сегодня</option>
              <option value="future">срок впереди</option>
              <option value="none">срока нет</option>
              <option value="has">срок назначен</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="bpes">Состояние</label>
            <select id="bpes" value={state} onChange={(e) => сСброса(setState)(e.target.value)}>
              <option value="open">открытые</option>
              <option value="closed">закрытые</option>
              <option value="all">все</option>
            </select>
          </div>
          <div className="f" style={{ minWidth: 170 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={left}
                     onChange={(e) => сСброса(setLeft)(e.target.checked)}
                     style={{ width: 'auto' }} />
              на тех, кто не работает
            </label>
          </div>
        </FilterBar>
      </div>

      {!готово ? (
        <div className="card">
          <div className="empty">
            {я.canPickOthers ? (
              <>
                <div className="empty-title">Выберите сотрудника</div>
                <div className="empty-hint">
                  Ваша учётка не привязана к сотруднику Bitrix, поэтому «мои» показать не с
                  кого — назовите человека сами. Появится привязка (её выставляет владелец
                  в учётке) — вкладка будет открываться сразу на вас.
                </div>
              </>
            ) : (
              <>
                <div className="empty-title">Учётка не привязана к сотруднику</div>
                <div className="empty-hint">
                  «Мои задания» — это задания сотрудника Bitrix, к которому привязана
                  учётка CRM, а у вашей привязки нет. Показывать чьи-нибудь чужие экран
                  не станет. Привязку выставляет владелец в учётке — скажите ему.
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <QueryState isLoading={зпр.isLoading} error={зпр.error}>
          <div className="card">
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Что нужно сделать</th>
                    <th className="nowrap">Вид</th>
                    <th>Кого ждём</th>
                    <th className="nowrap">Срок</th>
                    <th className="nowrap">Подписей</th>
                    <th className="nowrap">Процесс</th>
                    <th className="num">Ждёт</th>
                    <th className="nowrap">Ответ</th>
                  </tr>
                </thead>
                <tbody>
                  {(д?.rows ?? []).map((р) => (
                    <СтрокаЗадания key={р.id} р={р} спр={спр}
                                   открыть={() => setОткрыто(р.id)} />
                  ))}
                  {д && д.rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="empty">
                        <div className="empty-title">
                          {д.engine_started
                            ? 'Под эти условия не попало ни одно задание'
                            : 'Заданий нет, потому что движок ещё не запускали'}
                        </div>
                        <div className="empty-hint">{д.why_empty}</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {д?.sort_note && <div className="hint" style={{ marginTop: 8 }}>{д.sort_note}</div>}

            {д && д.total > СТРАНИЦА && (
              <div className="pager">
                <button disabled={start === 0 || зпр.isFetching}
                        onClick={() => setStart(Math.max(0, start - СТРАНИЦА))}>
                  ← назад
                </button>
                <span className="muted">
                  {start + 1}–{Math.min(start + СТРАНИЦА, д.total)} из {д.total}
                </span>
                <button disabled={д.next === null || зпр.isFetching}
                        onClick={() => setStart(д.next!)}>вперёд →</button>
              </div>
            )}
          </div>
        </QueryState>
      )}

      {открыто !== null && (
        <КарточкаЗадания id={открыто} спр={спр} закрыть={() => setОткрыто(null)} />
      )}
    </>
  )
}

function СтрокаЗадания({ р, спр, открыть }: {
  р: ЗаданиеСтрока
  спр?: Справочники
  открыть: () => void
}) {
  const срок = видСрока(р)
  const ушедшие = р.assignees_info.filter((ч) => ч.active === false)
  return (
    <tr>
      <td data-label="Что нужно сделать">
        {fmtText(р.name)}
        {р.instance_caption && (
          <div className="hint">
            {р.instance_caption}
          </div>
        )}
      </td>
      <td className="nowrap" data-label="Вид">
        {спр?.kind_names[р.kind ?? ''] ?? fmtText(р.kind)}
      </td>
      <td data-label="Кого ждём">
        {ктоЖдёт(р.assignees_info)}
        {ушедшие.length > 0 && (
          <span className="badge lose" style={{ marginLeft: 6 }}
                title="учётка отключена — движок обязан переназначить задание">
            не работает
          </span>
        )}
      </td>
      {/* Три разных состояния срока, и «срока нет» среди них спокойное. */}
      <td className="nowrap" data-label="Срок">
        <span className={срок.класс} title={срок.смысл}>{срок.ярлык}</span>
        {р.due_at && (
          <div className="hint">
            {fmtDate(р.due_at)}
          </div>
        )}
      </td>
      <td className="nowrap" data-label="Подписей">
        {р.answers_len} из {р.need_answers}
      </td>
      <td className="nowrap" data-label="Процесс">{fmtText(р.process)}</td>
      <td className="num" data-label="Ждёт">
        {р.age_days === null ? '—' : pluralN(р.age_days, 'день', 'дня', 'дней')}
      </td>
      <td className="nowrap" data-label="Ответ">
        <button type="button" onClick={открыть}>Открыть</button>
      </td>
    </tr>
  )
}

// ───────────────────────── карточка задания и форма ответа ─────────────────────────

function КарточкаЗадания({ id, спр, закрыть }: {
  id: number
  спр?: Справочники
  закрыть: () => void
}) {
  const зпр = useКарточка(id)
  const к = зпр.data

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>{к ? fmtText(к.row.name) : 'Задание'}</b>
        <span className="spacer" />
        <button type="button" onClick={закрыть}>Закрыть</button>
      </div>

      <QueryState isLoading={зпр.isLoading} error={зпр.error}>
        {к && (
          <>
            {к.row.body && <p style={{ whiteSpace: 'pre-wrap' }}>{к.row.body}</p>}

            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Кого ждём</th>
                    <th className="nowrap">Срок</th>
                    <th className="nowrap">Правило подписи</th>
                    <th className="nowrap">Процесс</th>
                    <th>Документ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td data-label="Кого ждём">{ктоЖдёт(к.row.assignees_info)}</td>
                    <td className="nowrap" data-label="Срок">
                      <span className={видСрока(к.row).класс}>{видСрока(к.row).ярлык}</span>
                      {к.row.due_at && (
                        <div className="muted">{fmtDate(к.row.due_at, true)}</div>
                      )}
                      {!к.row.due_at && к.row.no_due_reason && (
                        <div className="muted">{к.row.no_due_reason}</div>
                      )}
                    </td>
                    <td className="nowrap" data-label="Правило подписи">
                      {правилоПодписи(к.row)}
                    </td>
                    <td className="nowrap" data-label="Процесс">
                      {fmtText(к.row.process)}
                      {к.row.instance_state && (
                        <div className="muted">
                          {спр?.instance_state_names[к.row.instance_state]
                            ?? к.row.instance_state}
                        </div>
                      )}
                    </td>
                    <td data-label="Документ">
                      {fmtText(к.row.entity)} {fmtText(к.row.doc_id)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <ФормаОтвета к={к} спр={спр} />
            <Подписи ответы={к.answers} спр={спр} />
            <ИсторияЗадания события={к.events} />
          </>
        )}
      </QueryState>
    </div>
  )
}

/** Правило подписи — словами. Считает его движок, экран только называет. */
function правилоПодписи(р: ЗаданиеСтрока): string {
  if (р.mode === 'все') return 'подписывают все'
  if (р.mode === 'процент') return `нужно ${р.percent ?? 100} % подписей`
  return 'довольно одного'
}

function ФормаОтвета({ к, спр }: { к: Карточка; спр?: Справочники }) {
  return (
    <div style={{ marginTop: 16 }}>
      <b>Ответ</b>

      {/* Причина, по которой кнопка ниже не работает, стоит ВЫШЕ кнопки. Иначе человек
          нажмёт, ничего не произойдёт, и он уйдёт уверенный, что ответил. */}
      {!к.write_enabled && (
        <div className="notice" style={{ margin: '8px 0' }}>
          <b>Ответ отсюда пока не уходит.</b>
          <div style={{ marginTop: 6 }}>{к.write_why}</div>
        </div>
      )}

      {к.form.length === 0 ? (
        <div className="hint">
          Полей у этого задания нет: движок ждёт только решения, без заполнения формы.
        </div>
      ) : (
        <div style={{ ...РЯД, marginTop: 8 }}>
          {к.form.map((п) => (
            <div key={п.name ?? п.type} style={{ minWidth: 200 }}>
              <label className="hint" style={{ display: 'block', marginBottom: 4 }}
                     htmlFor={`bpef-${п.name ?? ''}`}>
                {fmtText(п.name)}{п.required ? ' *' : ''}
              </label>
              {/* Поле показано ровно тем видом, каким его спросит движок, и выключено:
                  вид поля — часть вопроса, а не украшение. */}
              {п.options ? (
                <select id={`bpef-${п.name ?? ''}`} disabled defaultValue="">
                  <option value="">— не выбрано —</option>
                  {п.options.map((в) => <option key={в} value={в}>{в}</option>)}
                </select>
              ) : (
                <input id={`bpef-${п.name ?? ''}`} disabled
                       defaultValue={п.default === undefined || п.default === null
                         ? '' : String(п.default)}
                       placeholder={спр?.field_type_names[п.type] ?? п.type} />
              )}
              {/* Тип, которого движок не знает, ПОМЕЧЕН, а не спрятан: спрятанное поле
                  означало бы, что человек ответил не на то, о чём его спросили. */}
              {!п.type_known && (
                <div className="hint">
                  Тип поля «{п.type}» этому разделу неизвестен — покажем его как есть.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button type="button" disabled={!к.write_enabled}
                title={к.write_enabled ? undefined : к.write_why}>
          Ответить
        </button>
        <span className="hint" style={{ marginLeft: 8 }}>
          {к.write_enabled ? '' : 'кнопка выключена: запись из CRM ещё не включена'}
        </span>
      </div>
    </div>
  )
}

function Подписи({ ответы, спр }: { ответы: Ответ[]; спр?: Справочники }) {
  return (
    <div style={{ marginTop: 16 }}>
      <b>Кто уже ответил</b>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Кто</th>
              <th className="nowrap">Что ответил</th>
              <th>Комментарий</th>
              <th className="nowrap">Когда</th>
            </tr>
          </thead>
          <tbody>
            {ответы.map((о) => (
              <tr key={о.who}>
                <td data-label="Кто">
                  {fmtText(о.who_name)}
                  {видЧеловека(о.who_active) && (
                    <span className="badge lose" style={{ marginLeft: 6 }}>не работает</span>
                  )}
                </td>
                <td className="nowrap" data-label="Что ответил">
                  {спр?.outcome_names[о.outcome ?? ''] ?? fmtText(о.outcome)}
                </td>
                <td data-label="Комментарий">{fmtText(о.comment)}</td>
                <td className="nowrap" data-label="Когда">{fmtDate(о.at, true)}</td>
              </tr>
            ))}
            {ответы.length === 0 && (
              <tr>
                <td colSpan={4} className="empty">
                  <div className="empty-title">Ещё никто не ответил</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * История задания: «было — стало — почему». Ради неё движок и завёл отдельную таблицу:
 * в Bitrix переназначение не оставляет следа вовсе, и задания уволенных висят молча.
 */
function ИсторияЗадания({ события }: { события: Событие[] }) {
  return (
    <div style={{ marginTop: 16 }}>
      <b>Что с заданием происходило</b>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th className="nowrap">Когда</th>
              <th>Что случилось</th>
              <th>Было у кого</th>
              <th>Стало у кого</th>
              <th>Почему</th>
            </tr>
          </thead>
          <tbody>
            {события.map((с) => (
              <tr key={`${с.task_id}-${с.ord}`}>
                <td className="nowrap" data-label="Когда">{fmtDate(с.at, true)}</td>
                <td data-label="Что случилось">{fmtText(с.kind)}</td>
                <td data-label="Было у кого">{fmtText(с.who_before_name)}</td>
                <td data-label="Стало у кого">
                  {с.who_after ? fmtText(с.who_after_name) : '— некому передать —'}
                </td>
                <td data-label="Почему">{fmtText(с.reason)}</td>
              </tr>
            ))}
            {события.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  <div className="empty-title">Задание никому не передавали</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ───────────────────────── вкладка «Кто чего ждёт» ─────────────────────────

function КтоЖдёт() {
  const зпр = useСводка()
  const д = зпр.data
  return (
    <QueryState isLoading={зпр.isLoading} error={зпр.error}>
      <div className="card">
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th className="num">Заданий</th>
                <th className="num">Согласовать</th>
                <th className="num">Ознакомиться</th>
                <th className="num">Заполнить</th>
                <th className="num">Срок прошёл</th>
                <th className="num">Срок сегодня</th>
                <th className="num">Без срока</th>
                <th className="num">Ждёт дольше всего</th>
              </tr>
            </thead>
            <tbody>
              {(д?.rows ?? []).map((р) => (
                <tr key={р.who}>
                  <td data-label="Сотрудник">
                    {fmtText(р.who_name)}
                    {видЧеловека(р.who_active) && (
                      <span className="badge lose" style={{ marginLeft: 6 }}>
                        не работает
                      </span>
                    )}
                  </td>
                  <td className="num" data-label="Заданий">{р.tasks}</td>
                  <td className="num" data-label="Согласовать">{р.approve}</td>
                  <td className="num" data-label="Ознакомиться">{р.review}</td>
                  <td className="num" data-label="Заполнить">{р.form}</td>
                  <td className="num" data-label="Срок прошёл">{р.overdue}</td>
                  <td className="num" data-label="Срок сегодня">{р.due_today}</td>
                  {/* Отдельным столбцом, а не в просрочке: это разные вещи. */}
                  <td className="num" data-label="Без срока">{р.no_due}</td>
                  <td className="num" data-label="Ждёт дольше всего">
                    {р.oldest_days === null
                      ? '—' : pluralN(р.oldest_days, 'день', 'дня', 'дней')}
                  </td>
                </tr>
              ))}
              {д && д.rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty">
                    <div className="empty-title">
                      {д.engine_started ? 'Открытых заданий нет'
                        : 'Заданий нет, потому что движок ещё не запускали'}
                    </div>
                    <div className="empty-hint">{д.why_empty}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {д && <div className="hint" style={{ marginTop: 8 }}>{д.sum_note}</div>}
      </div>

      {д && д.by_process.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <b>Чего ждёт каждый процесс</b>
          <div className="table-wrap cards" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  <th>Процесс</th>
                  <th className="num">Открытых заданий</th>
                  <th className="num">Экземпляров</th>
                  <th className="num">Срок прошёл</th>
                  <th className="num">Без срока</th>
                </tr>
              </thead>
              <tbody>
                {д.by_process.map((п) => (
                  <tr key={п.process ?? ''}>
                    <td data-label="Процесс">{fmtText(п.process)}</td>
                    <td className="num" data-label="Открытых заданий">{п.open_tasks}</td>
                    <td className="num" data-label="Экземпляров">{п.instances}</td>
                    <td className="num" data-label="Срок прошёл">{п.overdue}</td>
                    <td className="num" data-label="Без срока">{п.no_due}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </QueryState>
  )
}

// ───────────────────────── вкладка «Требует решения» ─────────────────────────

function Разбор() {
  const зпр = useРазбор()
  const д = зпр.data
  return (
    <QueryState isLoading={зпр.isLoading} error={зпр.error}>
      <div className="card">
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th className="nowrap">Когда</th>
                <th>Что случилось</th>
                <th>Где</th>
                <th>Пояснение</th>
                <th className="num">Ждёт</th>
              </tr>
            </thead>
            <tbody>
              {(д?.rows ?? []).map((р) => (
                <tr key={р.id}>
                  <td className="nowrap" data-label="Когда">{fmtDate(р.at, true)}</td>
                  <td data-label="Что случилось">{fmtText(р.kind)}</td>
                  <td data-label="Где">
                    {fmtText(р.process)}
                    {р.task_name && <div className="muted">{р.task_name}</div>}
                  </td>
                  <td data-label="Пояснение">{fmtText(р.note ?? р.ref)}</td>
                  <td className="num" data-label="Ждёт">
                    {р.age_days === null ? '—' : pluralN(р.age_days, 'день', 'дня', 'дней')}
                  </td>
                </tr>
              ))}
              {д && д.rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty">
                    <div className="empty-title">
                      {д.engine_started ? 'Разбирать нечего'
                        : 'Заданий нет, потому что движок ещё не запускали'}
                    </div>
                    <div className="empty-hint">{д.why_empty}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {д && д.stuck.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <b>Процессы, которые встали сами</b>
          <div className="hint" style={{ marginTop: 6 }}>{д.stuck_note}</div>
          <div className="table-wrap cards" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  <th>Процесс</th>
                  <th className="nowrap">Состояние</th>
                  <th>Где стоит</th>
                  <th className="num">Открытых заданий</th>
                  <th className="nowrap">Тронут</th>
                </tr>
              </thead>
              <tbody>
                {д.stuck.map((э) => (
                  <tr key={э.id}>
                    <td data-label="Процесс">{fmtText(э.process)}</td>
                    <td className="nowrap" data-label="Состояние">
                      <span className="badge lose">{fmtText(э.state)}</span>
                    </td>
                    <td data-label="Где стоит">{fmtText(э.caption)}</td>
                    <td className="num" data-label="Открытых заданий">{э.open_tasks}</td>
                    <td className="nowrap" data-label="Тронут">{fmtDate(э.updated_at, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </QueryState>
  )
}

// ───────────────────────── вкладка «Переназначения» ─────────────────────────

function История() {
  const [who, setWho] = useState('')
  const [start, setStart] = useState(0)
  const фильтры = useMemo(() => ({ who: who.trim() || undefined, start, limit: СТРАНИЦА }),
    [who, start])
  const зпр = useИстория(фильтры)
  const д = зпр.data

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="bpe-events">
          <div className="f" style={{ minWidth: 220 }}>
            <label htmlFor="bpeh">Кто (у кого забрали или кому отдали)</label>
            <input id="bpeh" value={who}
                   onChange={(e) => { setStart(0); setWho(e.target.value) }}
                   placeholder="идентификатор сотрудника" />
          </div>
        </FilterBar>
      </div>

      <QueryState isLoading={зпр.isLoading} error={зпр.error}>
        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">Когда</th>
                  <th>Что случилось</th>
                  <th>Было у кого</th>
                  <th>Стало у кого</th>
                  <th>Почему</th>
                </tr>
              </thead>
              <tbody>
                {(д?.rows ?? []).map((с) => (
                  <tr key={`${с.task_id}-${с.ord}`}>
                    <td className="nowrap" data-label="Когда">{fmtDate(с.at, true)}</td>
                    <td data-label="Что случилось">{fmtText(с.kind)}</td>
                    <td data-label="Было у кого">{fmtText(с.who_before_name)}</td>
                    <td data-label="Стало у кого">
                      {с.who_after ? fmtText(с.who_after_name) : '— некому передать —'}
                    </td>
                    <td data-label="Почему">{fmtText(с.reason)}</td>
                  </tr>
                ))}
                {д && д.rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      <div className="empty-title">
                        {д.engine_started ? 'Переназначений не было'
                          : 'Заданий нет, потому что движок ещё не запускали'}
                      </div>
                      <div className="empty-hint">{д.why_empty}</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {д && д.total > СТРАНИЦА && (
            <div className="pager">
              <button disabled={start === 0 || зпр.isFetching}
                      onClick={() => setStart(Math.max(0, start - СТРАНИЦА))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + СТРАНИЦА, д.total)} из {д.total}
              </span>
              <button disabled={д.next === null || зпр.isFetching}
                      onClick={() => setStart(д.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}
